import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import Pagination, { paginate } from 'components/Pagination/Pagination'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { IconFilter } from 'components/Icons/IconFilter'
import { useOnClickOutside } from 'utils/onClickOutside'
import { Menu } from 'components/Menu/Menu'
import Checkbox from 'components/Checkbox/Checkbox'

import { getPositionStatusBgColor, getPositionStatusColor } from 'utils/helpers'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'
import { MyPositionsContentLoader } from './ContentLoaders'
import { MobilePositionRow } from './MobilePositionRow'
import { DesktopPositionRow } from './DesktopPositionRow'
import { useUserPositionDisplayInfos } from 'utils/userUserPositionDisplayInfos'

const Container = styled.div`
  padding: 16px 0;
  padding-top: 0px;
  margin-top: 16px;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 16px 0;
  }
`

const Title = styled(Typography)`
  padding: 0 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${p => p.theme.colors.text.primary};
  display: flex;
  align-items: center;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding-left: 16px;
    padding-right: 16px;
    font-size: ${p => p.theme.typography.size.Description}px;
  }
`

const ChangeFilter = styled.div`
  position: relative;
  margin-left: auto;
  width: 300px;
  height: 48px;
  border-radius: 100px;
  border: 1px solid ${p => p.theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  & > div > svg {
    margin-left: 8px;
  }
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: auto;
    height: auto;
    padding: 8px 16px;
  }
`

const MenuItem = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${p => p.theme.colors.divider};
  &:hover {
    background-color: ${p => p.theme.colors.background.hover};
  }
`

const HeaderContainer = styled.div`
  padding: 16px 24px;
  display: flex;
  border-bottom: 1px solid ${p => p.theme.colors.divider};
  gap: 20px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const Th = styled(Typography)`
  flex-basis: 200px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Body}px;
  }
`

export const Status = styled.div<{ $type: 'active' | 'inactive' | 'closed' }>`
  height: 26px;
  width: 70px;
  border-radius: 4px;
  color: ${p => getPositionStatusColor(p.$type, p.theme)};
  background-color: ${p => getPositionStatusBgColor(p.$type, p.theme)};
  ${p =>
    p.$type === 'inactive' &&
    `
    border: 1px solid ${p.theme.colors.text.primary};
    box-sizing: border-box;
    `}
  font-size: ${p => p.theme.typography.size.Caption1}px;
  font-weight ${p => p.theme.typography.weight.Bold};
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: capitalize;
`

const PAGE_SIZE = 5

const FILTERS_KEY = 'my-positions-filters'

interface Props {
  positionInfos: PositionInfo[]
  onClick?: (positionId: string) => void
  activePositionId?: string
  queryStringPositionId?: string | null
  displayPairId: string
}

export const MyPositions = ({
  positionInfos,
  onClick,
  activePositionId,
  queryStringPositionId,
  displayPairId,
}: Props) => {
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem(FILTERS_KEY)
    const defaultFilters = { active: true, closed: true, inactive: true }
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters)
      if (parsedFilters?.inactive === undefined) {
        parsedFilters.inactive = true
      }
      return parsedFilters
    }
    return defaultFilters
  })

  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [displayPairId])

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handleFilterClick = useCallback(
    (filterName: keyof typeof filters) => {
      setFilters({
        ...filters,
        [filterName]: !filters[filterName],
      })
      setCurrentPage(1)
    },
    [filters]
  )

  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters))
  }, [filters])

  const { isLoading: isLoadingDisplayInfos, positionDisplayInfos } = useUserPositionDisplayInfos(positionInfos)

  const filteredItems = useMemo(
    () =>
      positionDisplayInfos.filter(position => {
        if (activePositionId && queryStringPositionId) {
          return activePositionId === position.position.id
        }
        if (filters.active && position.status === 'active') {
          return true
        }
        if (filters.closed && position.status === 'closed') {
          return true
        }
        if (filters.inactive && position.status === 'inactive') {
          return true
        }
        return false
      }),
    [positionDisplayInfos, filters, activePositionId, queryStringPositionId]
  )
  const paginatedItems = paginate(filteredItems, PAGE_SIZE, currentPage)

  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE)

  const isMobile = useIsMobile()

  return isLoadingDisplayInfos ? (
    <MyPositionsContentLoader />
  ) : (
    <Container>
      {isMobile && (
        <Title variant="label1" fontWeight="semiBold">
          My Positions
          <ChangeFilter onClick={toggleMenu}>
            <Typography
              variant={isMobile ? 'description' : 'description'}
              fontWeight="medium"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: 8 }}>Change Filter</span>
              <IconFilter isActive={!!Object.keys(filters).find(k => !filters[k])} />
            </Typography>
            {isMenuOpen && (
              <Menu anchorElement={menuNode} style={{ width: 300, zIndex: 1 }}>
                <MenuItem onClick={() => handleFilterClick('active')}>
                  Open <Checkbox checked={filters.active} />
                </MenuItem>
                <MenuItem onClick={() => handleFilterClick('inactive')}>
                  Inactive <Checkbox checked={filters.inactive} />
                </MenuItem>
                <MenuItem style={{ borderBottom: 'none' }} onClick={() => handleFilterClick('closed')}>
                  Closed <Checkbox checked={filters.closed} />
                </MenuItem>
              </Menu>
            )}
          </ChangeFilter>
        </Title>
      )}
      <HeaderContainer style={{ position: 'relative' }}>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 150 }}>
          Date
        </Th>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 100 }}>
          Pool
        </Th>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 100 }}>
          Status
        </Th>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 100 }}>
          Health
        </Th>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 200 }}>
          Range
        </Th>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 150 }}>
          Equity
        </Th>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 150 }}>
          APR
        </Th>
        <Th variant="description" fontWeight="semiBold" style={{ flexBasis: 150 }}>
          PNL
        </Th>
        {!isMobile && (
          <div onClick={toggleMenu} style={{ position: 'absolute', right: 38, top: 20, cursor: 'pointer' }}>
            <IconFilter isActive={!!Object.keys(filters).find(k => !filters[k])} />
            {isMenuOpen && (
              <Menu anchorElement={menuNode} style={{ width: 300, zIndex: 1, top: 32 }}>
                <MenuItem onClick={() => handleFilterClick('active')}>
                  Open <Checkbox checked={filters.active} />
                </MenuItem>
                <MenuItem onClick={() => handleFilterClick('inactive')}>
                  Inactive <Checkbox checked={filters.inactive} />
                </MenuItem>
                <MenuItem style={{ borderBottom: 'none' }} onClick={() => handleFilterClick('closed')}>
                  Closed <Checkbox checked={filters.closed} />
                </MenuItem>
              </Menu>
            )}
          </div>
        )}
      </HeaderContainer>

      {paginatedItems
        .map(di => {
          return {
            displayInfo: di,
          }
        })
        .map(({ displayInfo }, index) => {
          const positionInfo = positionInfos.find(p => p.positionId === displayInfo.position.id)
          if (!positionInfo) {
            return null
          }
          return isMobile ? (
            <MobilePositionRow
              key={displayInfo.position.id}
              index={index}
              onClick={onClick}
              paginatedItems={paginatedItems}
              activePositionId={activePositionId}
              displayInfo={displayInfo}
              positionInfo={positionInfo}
            />
          ) : (
            <DesktopPositionRow
              key={displayInfo.position.id}
              index={index}
              onClick={onClick}
              paginatedItems={paginatedItems}
              activePositionId={activePositionId}
              displayInfo={displayInfo}
              positionInfo={positionInfo}
            />
          )
        })}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        type="my-positions"
      />
    </Container>
  )
}
